import React, { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { withPrefix } from 'gatsby'
import { BlogPostCardInternalProps } from './BlogPostCard'
import { mobile } from '../../utils/styles/media'
import Tags from '../shared/Tags'
import { assignPrefix } from '../../constants'
import { abstractHtml } from '../../utils/get-html'
import stringBetween from '../../utils/stringBetween'

type DOMProps = { style?: React.CSSProperties; className?: string }
type PostCardProps = BlogPostCardInternalProps & DOMProps

const PostCard = ({ date, title, abstract, image, alt, href, authors, tags, expandable, ...props }: PostCardProps) => {
  const [isVisible, setVisible] = useState(true)
  const [stateInterval, setStateInterval] = useState<any>(null)
  const [fade, setFade] = useState(true)
  const postcardEl = useRef<any>()
  const postcardImg = useRef<any>()

  let centerText = ''
  if(abstract?.toString().includes('<center>')) {
     centerText = stringBetween('<center>', '</center>', abstract?.toString())
  }
  
  const checkForImage = () => {
    const imgRef = postcardImg.current
    if (imgRef && imgRef.complete && isVisible) setTimeout(() => setFade(false), 100)
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting)
      },
      { rootMargin: '150px' },
    )

    postcardEl.current && observer.observe(postcardEl.current)
    setStateInterval(setInterval(checkForImage, 200))
    return () => {
      if (postcardEl.current) observer.unobserve(postcardEl.current)
      if (stateInterval) clearInterval(stateInterval)
    }
  }, [])

  useEffect(() => {
    checkForImage()
  }, [postcardImg.current, isVisible])

  return (
    <WrapperLink href={withPrefix(href)} style={props.style} className={props.className} ref={postcardEl}>
      <Image expandable={expandable}>
        {isVisible && (
          <img
            src={assignPrefix(image)}
            className={fade ? 'postcard-cover hidden' : ' postcard-cover'}
            alt={alt || ''}
            ref={postcardImg}
          />
        )}
      </Image>
      <Texts>
        <div>
          <Title>{title}</Title>
          <Date>{date}</Date>
          {abstract && (
            <Abstract>
              <center dangerouslySetInnerHTML={{ __html: centerText }} />
              <div dangerouslySetInnerHTML={{ __html: abstractHtml(abstract.toString()) }}></div>
            </Abstract>
          )}
        </div>
        {tags && <Tags tags={tags} />}
      </Texts>
    </WrapperLink>
  )
}

export default PostCard

// Styles
const WrapperLink = styled.a<{ ref?: any }>`
  .postcard-cover {
    transition: opacity 100ms ease-in;
    opacity: 1;
    &.hidden {
      opacity: 0;
    }
  }
  height: 100%;
  display: inline-flex;
  flex-direction: column;
  border-radius: ${(p) => p.theme.radiusBig}px;
  background: white;
  user-select: none;
  overflow: hidden;
  margin: 0;
  transition: all 150ms ease;
  box-shadow: 0 2px 7px rgba(0, 0, 0, 0.05);

  ${mobile(
    css`
      margin: 0 0 20px;
    `,
  )}
`

const Image = styled.div<{ expandable?: boolean }>`
  width: 100%;
  height: 216px;
  flex-basis: auto;
  flex-shrink: 0;
  flex-grow: 0;
  ${(props) => (props.expandable ? '' : 'margin-right: 60px;')}

  overflow: hidden;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`

const Texts = styled.div`
  flex-grow: 1;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const Title = styled.h3`
  margin: 0 0 8px 0;
  font-size: 20px;
  line-height: 1.4;
  color: ${(p) => p.theme.primaryDark1};
`

const Abstract = styled.div`
  margin: 16px 0 0 0;
  font-size: 16px;
  line-height: 1.5;
  color: ${(p) => p.theme.primary};
  center {
    text-align: left;
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }
  p {
    margin: 0;
  }
`

const Date = styled.p`
  font-size: 16px;
  color: ${(p) => p.theme.primaryLight2};
  margin: 0;
`
